import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  limit,
  where,
  Timestamp,
  serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { Cull, CullQuery } from '../types';

// Helper function to remove undefined values from an object
const removeUndefinedValues = (obj: any): any => {
  const cleaned: any = {};
  for (const key in obj) {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  }
  return cleaned;
};

// Helper function to ensure user is authenticated
const ensureAuthenticated = () => {
  if (!auth.currentUser) {
    console.error('Auth error: No current user found');
    throw new Error('User must be authenticated to access this data');
  }
  console.log('Auth OK: User authenticated as', auth.currentUser.uid);
  return auth.currentUser;
};

const COLLECTION_NAME = 'culls';

export interface FirestoreCull extends Omit<Cull, 'id' | 'created_at' | 'updated_at'> {
  created_at: Timestamp;
  updated_at: Timestamp;
  user_id: string;
}

export const firestoreService = {
  async getCulls(params: CullQuery = {}): Promise<Cull[]> {
    try {
      let q = query(collection(db, COLLECTION_NAME));
      
      if (params.limit) {
        q = query(q, limit(params.limit));
      }
      
      q = query(q, orderBy('created_at', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const culls: Cull[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as FirestoreCull;
        culls.push({
          id: doc.id,
          ...data,
          created_by: data.user_id,
          created_at: data.created_at.toDate().toISOString(),
          updated_at: data.updated_at.toDate().toISOString(),
        });
      });
      
      return culls;
    } catch (error) {
      console.error('Error fetching culls:', error);
      throw error;
    }
  },

  async getCull(id: string): Promise<Cull> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        throw new Error('Cull not found');
      }
      
      const data = docSnap.data() as FirestoreCull;
      return {
        id: docSnap.id,
        ...data,
        created_by: data.user_id,
        created_at: data.created_at.toDate().toISOString(),
        updated_at: data.updated_at.toDate().toISOString(),
      };
    } catch (error) {
      console.error('Error fetching cull:', error);
      throw error;
    }
  },

  async createCull(cullData: Omit<Cull, 'id' | 'created_at' | 'updated_at'>, userId: string): Promise<Cull> {
    try {
      // Ensure user is authenticated
      const currentUser = ensureAuthenticated();
      
      // Verify the userId matches the authenticated user
      if (currentUser.uid !== userId) {
        throw new Error('Access denied: You can only create records for yourself');
      }
      const now = serverTimestamp();
      const cleanedData = removeUndefinedValues({
        ...cullData,
        user_id: userId,
        created_at: now,
        updated_at: now,
      });
      const docRef = await addDoc(collection(db, COLLECTION_NAME), cleanedData);
      
      return await this.getCull(docRef.id);
    } catch (error) {
      console.error('Error creating cull:', error);
      throw error;
    }
  },

  async updateCull(id: string, cullData: Partial<Omit<Cull, 'id' | 'created_at' | 'updated_at'>>): Promise<Cull> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      const cleanedData = removeUndefinedValues({
        ...cullData,
        updated_at: serverTimestamp(),
      });
      await updateDoc(docRef, cleanedData);
      
      return await this.getCull(id);
    } catch (error) {
      console.error('Error updating cull:', error);
      throw error;
    }
  },

  async deleteCull(id: string): Promise<void> {
    try {
      const docRef = doc(db, COLLECTION_NAME, id);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting cull:', error);
      throw error;
    }
  },

  async getUserCulls(userId: string, params: CullQuery = {}): Promise<Cull[]> {
    try {
      // Ensure user is authenticated
      const currentUser = ensureAuthenticated();
      
      // Verify the userId matches the authenticated user
      if (currentUser.uid !== userId) {
        throw new Error('Access denied: You can only access your own records');
      }
      
      let q = query(
        collection(db, COLLECTION_NAME),
        where('user_id', '==', userId)
      );
      
      if (params.limit) {
        q = query(q, limit(params.limit));
      }
      
      q = query(q, orderBy('created_at', 'desc'));
      
      const querySnapshot = await getDocs(q);
      const culls: Cull[] = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data() as FirestoreCull;
        culls.push({
          id: doc.id,
          ...data,
          created_by: data.user_id,
          created_at: data.created_at.toDate().toISOString(),
          updated_at: data.updated_at.toDate().toISOString(),
        });
      });
      
      return culls;
    } catch (error) {
      console.error('Error fetching user culls:', error);
      throw error;
    }
  }
};